import { useState, useRef, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle, KeyRound, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'

interface ManagerHandoffModalProps {
  open: boolean
  onClose: () => void
}

/**
 * 4-digit PIN modal for Daily Closing handoff. A counter cashier hands
 * the device to a manager; manager types their PIN. On success the
 * backend mints a fresh JWT for the manager, the frontend swaps the
 * stored auth state, and we navigate to /admin/day-close so the close
 * wizard is one tap away. Visual pattern mirrors PinEntryModal but the
 * payload + side-effects are different (session swap, not void item).
 */
export function ManagerHandoffModal({ open, onClose }: ManagerHandoffModalProps) {
  const [pin, setPin] = useState(['', '', '', ''])
  const [error, setError] = useState('')
  const [shake, setShake] = useState(false)
  const [successName, setSuccessName] = useState<string | null>(null)
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ]

  useEffect(() => {
    if (open) {
      setPin(['', '', '', ''])
      setError('')
      setSuccessName(null)
      setTimeout(() => inputRefs[0].current?.focus(), 50)
    }
  }, [open])

  const elevateMutation = useMutation({
    mutationFn: async () => {
      const pinStr = pin.join('')
      return apiClient.elevateSession(pinStr)
    },
    onSuccess: (res) => {
      if (res.success && res.data) {
        apiClient.setAuth(res.data.token, res.data.user)
        const name =
          [res.data.user.first_name, res.data.user.last_name]
            .filter(Boolean)
            .join(' ')
            .trim() || res.data.user.username
        setSuccessName(name)
        setTimeout(() => {
          onClose()
          // Hard navigation so React Query caches refetch with the new JWT
          // (otherwise stale cashier-scoped responses linger in memory).
          window.location.href = '/admin/day-close'
        }, 900)
      } else {
        triggerShake("PIN didn't match")
      }
    },
    onError: () => {
      triggerShake("PIN didn't match")
    },
  })

  const triggerShake = (msg: string) => {
    setError(msg)
    setShake(true)
    setPin(['', '', '', ''])
    setTimeout(() => setShake(false), 500)
    setTimeout(() => inputRefs[0].current?.focus(), 100)
  }

  const handleDigit = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return
    const newPin = [...pin]
    newPin[index] = value
    setPin(newPin)
    setError('')
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus()
    }
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleSubmit = () => {
    const pinStr = pin.join('')
    if (pinStr.length !== 4) {
      setError('Enter all 4 digits')
      return
    }
    elevateMutation.mutate()
  }

  if (!open) return null

  if (successName) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
        <div className="w-full max-w-sm rounded-2xl bg-white p-8 text-center dark:bg-zinc-900">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-950">
            <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h3 className="text-xl font-bold">Signed in as {successName}</h3>
          <p className="mt-2 text-muted-foreground">Opening Daily Closing…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-sm max-h-[90dvh] overflow-y-auto overflow-x-hidden rounded-2xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="flex items-center justify-between bg-sky-50 p-4 dark:bg-sky-950/40">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            <h3 className="font-bold text-sky-900 dark:text-sky-100">Manager PIN required</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b p-4 text-sm text-muted-foreground">
          Hand the device to a manager so they can finish Daily Closing for you.
        </div>

        <div className="p-6">
          <label className="mb-3 block text-center text-sm font-medium text-foreground/80">
            Enter Manager PIN
          </label>
          <div className={`flex justify-center gap-3 ${shake ? 'animate-shake' : ''}`}>
            {pin.map((digit, i) => (
              <input
                key={i}
                ref={inputRefs[i]}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigit(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="h-14 w-14 rounded-xl border-2 border-input bg-background text-foreground text-center text-2xl font-bold focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:focus:ring-sky-900"
              />
            ))}
          </div>
          {error && <p className="mt-3 text-center text-sm text-rose-500">{error}</p>}
        </div>

        <div className="flex gap-3 border-t p-4">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-sky-600 text-white hover:bg-sky-700"
            onClick={handleSubmit}
            disabled={pin.some((d) => d === '') || elevateMutation.isPending}
          >
            {elevateMutation.isPending ? 'Verifying…' : 'Unlock Daily Closing'}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out 2; }
      `}</style>
    </div>
  )
}
